import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import { Box } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { stackLayers } from '../../shared/layer-stack'
import type { LayerUrls } from '../../shared/types'

export interface LayerThumbProps {
  layers: LayerUrls
  alt: string
  size?: number
  dimmed?: boolean
}

export default function LayerThumb({
  layers,
  alt,
  size = 96,
  dimmed = false,
}: LayerThumbProps) {
  const { formatMessage } = useIntl()
  const [broken, setBroken] = useState(false)
  const stack = stackLayers(layers)

  const frame = {
    width: size,
    height: size,
    borderRadius: 1,
    bgcolor: 'action.hover',
  }

  if (!stack.length || broken) {
    return (
      <Box
        role="img"
        aria-label={formatMessage(
          { id: 'avatarStudio.thumb.noArtwork' },
          { name: alt },
        )}
        sx={{
          ...frame,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
        }}
      >
        <ImageNotSupportedOutlinedIcon fontSize="small" aria-hidden={true} />
      </Box>
    )
  }

  return (
    <Box
      role="img"
      aria-label={alt}
      sx={{
        ...frame,
        position: 'relative',
        overflow: 'hidden',
        opacity: dimmed ? 0.45 : 1,
        filter: dimmed ? 'grayscale(1)' : 'none',
      }}
    >
      {stack.map((layer) => (
        // A plain img, not Box: MUI consumes width/height as style props and never emits the
        // attributes, which are what reserve the space before the layer loads.
        <img
          key={layer.role}
          src={layer.url}
          alt=""
          aria-hidden={true}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            mixBlendMode: layer.blend,
          }}
        />
      ))}
    </Box>
  )
}
